import './index.scss'
import {useEffect, useState} from 'react'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageTitle} from '../../components/core/title'
import {JobChart, JobTable} from '../../components/pages/job'
import Footer from '../../components/layouts/footer'
import {ImgButton} from '../../components/core/button'
import {
    ActivityTrackerIcon,
    EditIcon,
    PausedIcon,
    PlayIcon,
    PlusIcon,
    ReminderIcon,
    SecureJobsIcon,
    TrashIcon
} from '../../icons'
import {routes} from '../../routes'
import NavigationHelper from '../../utils/navigation'
import {Link, useNavigate, useParams} from 'react-router-dom'
import DataSwitcher from '../../components/core/dataSwitcher'
import classNames from 'classnames'
import {useDispatch, useSelector} from 'react-redux'
import {deleteJob, getJob, getJobEvents, getJobs, getJobStatistics, updateJob} from '../../stores/heartbeats'
import Modal from '../../components/core/modal'
import JobModal from '../jobs/JobModal'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import SectionLoader from '../../components/core/sectionLoader'
import localStorageKeys from '../../utils/localStorageKeys'
import Switch from '../../components/core/switch'
import localStorageManager from '../../utils/localStorageManager'
import Customer from '../../utils/customer'
import Breadcrumb from '../../components/core/breadcrumb'
import {colors} from '../../utils/colors'
import UrlContainer from '../../components/pages/job/Instructions/UrlContainer'
import DateInterval from '../../components/pages/dateInterval'
import {setRangeSelected} from '../../stores/monitoring'
import {DateFormats, formatDate, TZ} from '../../utils/date'
import EmptyContent from '../resources/EmptyContent'
import Badge from '../../components/core/badge'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import limitUsageManager from '../../utils/hooks/limitUsageManager'
import {getAllLimitUsages} from '../../stores/auth'
import Tooltip from '../../components/core/tooltip'
import ResourceState from '../../components/pages/resource/ResourceState'
import {getTooltipText} from '../jobs'
import IncidentsModal from '../../components/pages/ResourceIncidents'
import Pulse from '../../components/core/pulse'
import limitManager from '../../utils/limitManager'
import {getIntervalUnitAndValue} from '../../utils/intervalManagerService'
import TextHelper from '../../utils/text'
import renderSkeletonLoaders from '../../utils/loader'
import statusCodes from '../../utils/http'
import {isNewFeatureEnabled} from '../../utils/newFeature'
import Modals from '../../components/pages/serverMonitoring/Modals'
import modalTypes from '../../components/pages/serverMonitoring/modalTypes.json'
import cronJobRulesetTypes from '../../data/cronJobRulesetTypes.json'
import urgencyOptions from '../../data/selectOptions/urgency.json'
import CustomSelect from '../../components/core/customSelect'
import Checkbox from '../../components/core/checkbox'
import Textarea from '../../components/core/textarea'
import Input from '../../components/core/input'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const RANGE = 'range'
const URL_TYPE = ':heartbeat_url'
const HEARTBEAT_MONITORING = 'heartbeat_monitoring'
const METHOD_GET = 'GET'

const {liveUpdateTime, isReadOnlyModeEnabled} = envConfig

const Job = () => {
    const {id} = useParams()
    const text = useNamespaceTranslations('job')
    const conditionTypes = useNamespaceTranslations('condition-types')?.resource_conditions
    const intervalUnits = useNamespaceTranslations('interval-units')?.interval_units
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const data = text.programming_languages
    const rulesetValues = text.ruleset_values
    const dispatch = useDispatch()
    const [isFetchingJob, setIsFetchingJob] = useState(true)
    const [isFetching, setIsFetching] = useState(false)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [isDeleting, setIsDeleting] = useState(false)
    const [jobs, setJobs] = useState([])
    const [heartbeat, setHeartbeat] = useState(null)
    const [statistics, setStatistics] = useState()
    const [events, setEvents] = useState([])
    const [jobModal, setJobModal] = useState(false)
    const [instructionModal, setInstructionModal] = useState(false)
    const [chartLoader, setChartLoader] = useState(true)
    const [isButtonLoading, setIsButtonLoading] = useState(false)
    const [isJobEventFetching, setIsJobEventFetching] = useState(true)
    const [activeModal, setActiveModal] = useState({type: null})
    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(localStorageManager.get(localStorageKeys.IS_HEARTBEAT_AUTHOREFRESH_ENABLED))
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const services = useSelector(state => state.resources.services)
    const rulesets = useSelector(state => state.heartbeats.rulesets)
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const currentDate = formatDate(new Date(), DateFormats.ISO_DATETIME)
    const navigate = useNavigate()
    const limitUsages = {
        heartbeat: limitUsageManager(usageLimitsData.limit_modules.monitoring.module, usageLimitsData.limit_keys.heartbeat_count.limit_key, organisationLimitUsages),
        interval: limitUsageManager(usageLimitsData.limit_modules.incident_management.module, usageLimitsData.limit_keys.heartbeat_incident_ruleset_count.limit_key, organisationLimitUsages),
        incident_ruleset: limitUsageManager(usageLimitsData.limit_modules.incident_management.module, usageLimitsData.limit_keys.heartbeat_incident_ruleset_count.limit_key, organisationLimitUsages)
    }

    const isLimitExceeded = limitUsages.heartbeat.isLimitExceeded
    const isIntervalLimitExceeded = limitUsages.interval.isLimitExceeded

    const isHeartbeatMonitoringEnabled = limitManager.getHeartbeatMonitoringIsEnabled(usageLimitsData)
    const tooltipText = getTooltipText(isLimitExceeded, heartbeat, limitUsages.heartbeat.title, text)

    const interval = getIntervalUnitAndValue(heartbeat?.settings?.interval, intervalUnits)
    const intervalDetails = interval?.unitValue ? `${interval.unitValue} ${interval?.unitLabel + TextHelper.pluralize(interval.unitValue)}` : text.not_available

    const toggleModal = () => {
        setJobModal(!jobModal)
    }

    const deleteHeartbeat = async() => {
        setIsDeleting(true)
        await dispatch(deleteJob([heartbeat?.id]))
        setIsDeleting(false)
        dispatch(setConfirmIsActive(false))
        navigate(NavigationHelper.getLocalizedPath(routes.jobs.paths))
    }

    const getJobData = async(params = {}) => {
        if (!isHeartbeatMonitoringEnabled) return

        const dates = params?.dates?.[0]
        const newParams = {
            interval: params?.interval,
            start_date: formatDate(params?.startDate || dates?.startDate, DateFormats.ISO_DATETIME, TZ.UTC),
            end_date: formatDate(params?.endDate || dates?.endDate, DateFormats.ISO_DATETIME, TZ.UTC)
        }

        const data = await dispatch(getJobStatistics(id, newParams, navigate))
        setStatistics(data)
    }

    const fetchJob = async() => {
        const data = await dispatch(getJob(id, navigate))
        setHeartbeat(data)
        setIsFetchingJob(false)
    }
    const fetchJobEvents = async() => {
        const {data, count} = await dispatch(getJobEvents(id, navigate))
        setEvents({
            data,
            count
        })
        setIsJobEventFetching(false)
    }

    const fetchJobs = async() => {
        const data = await dispatch(getJobs())
        setJobs(data)
    }

    const fetchData = async(showLoader, showChartLoader = false,  data = null) => {
        setIsFetching(showLoader)
        setChartLoader(showChartLoader)
        if (!services?.length) {
            return
        }
        try {
            if (!data) {
                const initialParams = {
                    startDate: currentDate,
                    endDate: currentDate,
                    interval: '7d'
                }
                await Promise.all([fetchJobs(), fetchJob(), getJobData(initialParams), fetchJobEvents()])
                return
            }

            const newParams = {
                startDate: data?.interval === RANGE ? data?.startDate : currentDate,
                endDate: data?.interval === RANGE ? data?.endDate : currentDate,
                interval: data?.interval
            }
            await getJobData(newParams)
        } catch (e) {
            if (statusCodes.SpecialErrors.includes(e?.response?.status)) {
                navigate(NavigationHelper.getLocalizedPath(routes.not_found.paths))
            }
        } finally {
            setIsFetching(false)
            setChartLoader(false)
        }
    }

    const handleAutorefresh = e => {
        if (e.target.checked) {
            setIsAutoRefreshEnabled(true)
            localStorage.setItem(localStorageKeys.IS_HEARTBEAT_AUTHOREFRESH_ENABLED, true)
            return
        }
        setIsAutoRefreshEnabled(false)
        localStorage.setItem(localStorageKeys.IS_HEARTBEAT_AUTHOREFRESH_ENABLED, false)
    }

    const handleRulesetModal = () => {
        if (!isNewFeatureEnabled()) return
        setActiveModal({type: modalTypes.ruleset_list})
    }

    const handleChangeStatus = async(enabled) => {
        setIsButtonLoading(true)
        await dispatch(updateJob(heartbeat?.id, ({...heartbeat, interval: heartbeat?.settings?.interval, enabled: enabled})))
        setIsButtonLoading(false)
        await fetchJob()
    }

    useEffect(() => {
        if (isHeartbeatMonitoringEnabled) {
            fetchData(true)
            dispatch(setRangeSelected(false))
            dispatch(getAllLimitUsages())
        }
        setIsFirstLoad(false)
    }, [id, services])

    useEffect(() => {
        const interval = setInterval(() => {
            heartbeat?.id && dispatch(getJob(heartbeat?.id, navigate))
        }, liveUpdateTime)

        return () => clearInterval(interval)
    }, [heartbeat])

    const getRulesetFormInitialValues = () => {
        return {
            ...Object.fromEntries(
                Object.entries(rulesetValues).map(([key, item]) => [
                    key,
                    {
                        ...item,
                        FormItem:
                            item?.form_type === 'select' ? CustomSelect :
                                item?.form_type === 'checkbox' ? Checkbox :
                                    item?.form_type === 'textarea' ? Textarea :
                                        Input,
                        ...(item?.form_type === 'select' && {
                            options: item?.name === 'type' ? Object.values(cronJobRulesetTypes) :
                                item?.name === 'urgency' ? Object.values(urgencyOptions) :
                                    [],
                            value:
                                item?.name === 'type' ? cronJobRulesetTypes.result :
                                    item?.name === 'urgency' ? urgencyOptions.low :
                                        ''
                        })
                    }
                ])
            ),
            conditions: {
                ...rulesetValues.conditions,
                pairs: rulesetValues.conditions.pairs.map(condition => {
                    return {
                        key: {
                            ...condition.key,
                            FormItem: CustomSelect,
                            options: [cronJobRulesetTypes.result],
                            value: cronJobRulesetTypes.result
                        },
                        operator: {
                            ...condition.operator,
                            FormItem: CustomSelect,
                            options: Object.values(conditionTypes.json_key_value_check.values).filter(
                                conditionType => condition.key.value === cronJobRulesetTypes.result.value ?
                                    conditionType?.value === conditionTypes.json_key_value_check.values.equal :
                                    conditionType?.value === conditionTypes.json_key_value_check.values.contains
                            ),
                            value: conditionTypes.json_key_value_check.values.equal
                        },
                        values: {
                            ...condition.values,
                            FormItem:  CustomSelect,
                            options: Object.values(cronJobRulesetTypes.result.condition.options),
                            value: cronJobRulesetTypes.result.condition.options.default
                        }
                    }
                })
            }
        }
    }

    return (
        <main className={classNames('main job-container monitoring', {
            dark: isDarkMode
        })}>
            {activeModal?.type && <Modals
                isVisible={activeModal?.type}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
                uiConfig={{isDarkMode}}
                rulesetConfig={{
                    rulesetData: rulesets,
                    rulesetFormInitialValues: getRulesetFormInitialValues(),
                    usageLimits: limitUsages,
                    rulesetTypeOptions: cronJobRulesetTypes,
                    isCronJobRuleset: true
                }}
            />
            }
            <Modal
                closeModal={toggleModal}
                contentClassName='resource-modal-content interval-based-ruleset'
                isOpen={jobModal}
                title={jobModal?.isEdit ? text.edit_job : text.add_new_job}>
                <JobModal modal={jobModal} jobsLength={jobs?.length} closeModal={setJobModal} heartbeat={heartbeat}
                    callback={() => fetchJob(false)} />
            </Modal>
            <Confirm
                body={text.are_you_sure}
                callback={deleteHeartbeat} showLoader={isDeleting}/>
            <div className="content-container">
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <Breadcrumb data={[text.section, routes.jobs]}/>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {isHeartbeatMonitoringEnabled && Customer.isOwnTeam() && (
                        <ImgButton onClick={toggleModal} disabled={isReadOnlyModeEnabled}>
                            {text.add_job}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </div>
                {
                    !isHeartbeatMonitoringEnabled ?
                        <EmptyContent message={text.disabled_heartbeat_message} hideButton={true} icon={<SecureJobsIcon/>}/> :
                        <>
                            <div className="auto-refresh-container">
                                {isFirstLoad ? renderSkeletonLoaders(1, 'switch-skeleton') : <div className="auto-refresh">
                                    <p>{text.auto_refresh}</p>
                                    <Switch name="auto-refresh" onChange={handleAutorefresh}
                                        checked={isAutoRefreshEnabled}/>
                                </div>}
                            </div>
                            <div className="search-filter-actions">
                                <div className="header-container">
                                    {isFetchingJob ?
                                        renderSkeletonLoaders(1) :
                                        <div className="position-relative">
                                            <div className="d-flex gap-1">
                                                <div className={classNames('resource-name', {
                                                    dark: isDarkMode,
                                                    'gap-half': heartbeat?.settings?.interval
                                                })} id="job-resource-name">
                                                    <ResourceState
                                                        tooltipText={
                                                            !heartbeat?.settings?.interval
                                                                ? text.warning_text
                                                                : heartbeat?.has_open_incidents
                                                                    ? text.down
                                                                    : text.up
                                                        }
                                                        icon={
                                                            heartbeat?.settings?.interval && (
                                                                <Pulse color={heartbeat?.has_open_incidents ? 'red' : ''} />
                                                            )
                                                        }
                                                    />
                                                    <h2>{heartbeat?.name}</h2>
                                                    <DataSwitcher>
                                                        {jobs?.map((item, index) => (
                                                            <Link
                                                                to={NavigationHelper.getNavigationUrl(NavigationHelper.getLocalizedPath(routes.job.paths),
                                                                    ':id', item?.heartbeat_id)}
                                                                key={index}
                                                                className={classNames({
                                                                    'popover-item': true,
                                                                    active: id === item?.heartbeat_id
                                                                })}>{item.name}</Link>
                                                        ))}
                                                    </DataSwitcher>
                                                </div>
                                            </div>
                                        </div>}
                                    {Customer.isOwnTeam() && (
                                        <div className="selected-actions">
                                            {isFirstLoad ?
                                                renderSkeletonLoaders(1, 'btn-skeleton responsive-add-button') :
                                                <ImgButton onClick={toggleModal}
                                                    className="responsive-add-button">
                                                    <span><PlusIcon/></span>
                                                </ImgButton>}
                                            {isFirstLoad ?
                                                renderSkeletonLoaders(1, 'btn-skeleton')
                                                : !heartbeat?.enabled && (isLimitExceeded || (isIntervalLimitExceeded && heartbeat?.settings?.interval)) ?
                                                    <Tooltip content={tooltipText} trigger='mouseenter' placement='top'>
                                                        <ImgButton
                                                            className={classNames({
                                                                'light-bg': !isDarkMode
                                                            })}
                                                            disabled={true}
                                                            onClick={() => handleChangeStatus({...heartbeat, enabled: true})}>
                                                            <span className="d-flex"><PlayIcon /></span>
                                                            <span>{text.enable}</span>
                                                        </ImgButton>
                                                    </Tooltip> :
                                                    <ImgButton className={classNames({
                                                        'light-bg': !isDarkMode
                                                    })}
                                                    disabled={!isHeartbeatMonitoringEnabled || isReadOnlyModeEnabled}
                                                    onClick={() => handleChangeStatus(!heartbeat.enabled)}>
                                                        <span>{isButtonLoading ? <ButtonLoader/> : heartbeat?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                                                        <span className="button-text">{heartbeat?.enabled ? text.disable : text.enable}</span>
                                                    </ImgButton>}
                                            {isFirstLoad ?
                                                renderSkeletonLoaders(1, 'btn-skeleton') :
                                                <ImgButton className={classNames({
                                                    'light-bg': !isDarkMode
                                                })}
                                                onClick={() => setJobModal({isEdit: true})} disabled={isReadOnlyModeEnabled}>
                                                    <span><EditIcon/></span>
                                                    <span className="button-text">{text.edit}</span>
                                                </ImgButton>}
                                            {isFirstLoad ?
                                                renderSkeletonLoaders(1, 'btn-skeleton') :
                                                <ImgButton className={classNames('outline-red', {
                                                    'light-bg': !isDarkMode
                                                })}
                                                onClick={() => dispatch(setConfirmIsActive(true))}
                                                disabled={isReadOnlyModeEnabled}>
                                                    <span><TrashIcon stroke={colors.colorRed}/></span>
                                                    <span className="button-text">{text.delete}</span>
                                                </ImgButton>}
                                        </div>)}
                                </div>
                                <UrlContainer url={heartbeat?.url} urlType={URL_TYPE} data={data} urlTitle={text.instructions} method={METHOD_GET} instructionModal={instructionModal} setInstructionModal={setInstructionModal} isLoading={isFetchingJob}/>
                                <div className="job-action-container">
                                    <DateInterval
                                        resetFn={() => fetchData(false, true)}
                                        callbackFn={(data) => fetchData(false, data?.showLoader, data)}
                                        type={HEARTBEAT_MONITORING}
                                        isAutoRefreshEnabled={isAutoRefreshEnabled}
                                        isLoading={isFirstLoad}/>
                                </div>
                            </div>
                            <div id='chart-overview'>
                                <div className={classNames({
                                    'content-body loader-container': true,
                                    'has-loader': chartLoader || isFetching,
                                    dark: isDarkMode
                                })}>
                                    <SectionLoader loader={chartLoader || isFetching}/>
                                    <span className='content-body-header'>
                                        <h3 className="content-body-text" id="job-chart-text">{text.chart_title}</h3>
                                        <Tooltip content={text.coming_soon} placement='top' trigger='mouseenter'>
                                            <span className='coming-soon-text' onClick={handleRulesetModal}>
                                                {text.see_rulesets}
                                                <ReminderIcon width={20} height={18} stroke={isDarkMode ? colors.colorBgFilter : colors.colorMuted} />
                                            </span>
                                        </Tooltip>
                                    </span>
                                    <JobChart statistics={statistics} name={heartbeat?.name}/>
                                </div>
                                <div className={classNames('job-details', {
                                    dark: isDarkMode
                                })}>
                                    <p className='job-details-text'>{text.heartbeat_details}</p>
                                    {isFetching ?
                                        <span className='content-body-loading'>{renderSkeletonLoaders(1, 'textarea-skeleton-sm')}</span> :
                                        <div className='job-details-box'>
                                            <p className="box-title">{text.expected_interval}</p>
                                            <span className="box-body">
                                                {heartbeat?.settings?.interval
                                                    ? intervalDetails
                                                    : text.not_available}
                                            </span>
                                            <span className="box-body">
                                                <Badge color={!heartbeat?.settings?.interval ? text.warning : text.success}>
                                                    {!heartbeat?.settings?.interval ? text.disabled : text.enabled}
                                                </Badge>
                                            </span>
                                        </div>}
                                </div>
                            </div>
                            <div className={classNames({
                                'content-body chart-container': true,
                                dark: isDarkMode
                            })}>
                                {isJobEventFetching ? <span className='content-body-loading'>{renderSkeletonLoaders(6, 'select-skeleton')}</span> :
                                    events?.data?.length > 0 ? <JobTable events={events} caption={text.latest_activity} jobId={id}/> : (
                                        <div className="empty-content">
                                            <ActivityTrackerIcon/>
                                            <span>{text.events_not_found}</span>
                                        </div>
                                    )}
                            </div>
                            <div
                                className={classNames({
                                    'content-body chart-container': true,
                                    dark: isDarkMode
                                })}
                            >
                                <h3 className="incidents-table-title">
                                    {text.incidents}
                                </h3>
                                <IncidentsModal
                                    resourceId={heartbeat?.id}
                                    resourceType="Heartbeat"
                                    isModal={false}
                                />
                            </div>

                        </>
                }
            </div>
            <Footer/>
        </main>
    )
}

export default Job
